import type { Client, ClientLifecycleStatus } from '~/types/models';

export type ClientWorkspaceBucket = 'leads' | 'opportunities' | 'clients' | 'past-clients';

const LEAD_STATUSES = new Set<ClientLifecycleStatus>(['raw_enquiry', 'lead', 'prospect']);
const OPPORTUNITY_STATUSES = new Set<ClientLifecycleStatus>(['qualified', 'opportunity', 'proposal_sent']);
const CLIENT_STATUSES = new Set<ClientLifecycleStatus>(['won', 'onboarding', 'active']);
const PAST_CLIENT_STATUSES = new Set<ClientLifecycleStatus>(['paused', 'completed', 'past', 'lost', 'archived']);

export function normalizeWorkspaceClientStatus(value: unknown): ClientLifecycleStatus | undefined {
    const normalized = String(value || '').trim() as ClientLifecycleStatus;
    return normalized
        && (
            LEAD_STATUSES.has(normalized)
            || OPPORTUNITY_STATUSES.has(normalized)
            || CLIENT_STATUSES.has(normalized)
            || PAST_CLIENT_STATUSES.has(normalized)
        )
        ? normalized
        : undefined;
}

export function deriveWorkspaceClientStatus(client: Pick<Client, 'lifecycle'>): ClientLifecycleStatus {
    const lifecycle = client.lifecycle || {};
    const explicitStatus = normalizeWorkspaceClientStatus(lifecycle.status);
    if (explicitStatus) {
        return explicitStatus;
    }

    const deliveryStage = String(lifecycle.deliveryStage || '').trim();
    const pipelineStage = String(lifecycle.pipelineStage || '').trim();
    const commercialStage = String(lifecycle.commercialStage || '').trim();

    if (deliveryStage === 'complete' || commercialStage === 'past_client') return 'past';
    if (deliveryStage === 'paused') return 'paused';
    if (deliveryStage === 'active') return 'active';
    if (deliveryStage === 'onboarding') return 'onboarding';

    switch (pipelineStage) {
        case 'new_enquiry':
            return 'raw_enquiry';
        case 'contacted':
            return 'prospect';
        case 'qualified':
            return 'qualified';
        case 'proposal_sent':
            return 'proposal_sent';
        case 'negotiation':
            return 'opportunity';
        case 'won':
            return 'won';
        case 'lost':
            return 'lost';
        default:
            break;
    }

    if (commercialStage === 'client') return 'active';
    if (commercialStage === 'opportunity') return 'opportunity';
    return 'lead';
}

export function classifyClientWorkspaceBucket(client: Pick<Client, 'lifecycle'>): ClientWorkspaceBucket {
    const status = deriveWorkspaceClientStatus(client);

    if (LEAD_STATUSES.has(status)) return 'leads';
    if (OPPORTUNITY_STATUSES.has(status)) return 'opportunities';
    if (CLIENT_STATUSES.has(status)) return 'clients';
    return 'past-clients';
}

function resolveClientDateValue(value: unknown): number {
    if (!value) return 0;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
        return (value as { toDate: () => Date }).toDate().getTime();
    }
    if (typeof value === 'object' && value !== null && 'seconds' in value) {
        return Number((value as { seconds?: number }).seconds || 0) * 1000;
    }

    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

export function sortClientsForWorkspace<T extends Pick<Client, 'updatedAt' | 'createdAt'>>(clients: T[]): T[] {
    return [...clients].sort((left, right) => {
        const rightDate = resolveClientDateValue(right.updatedAt) || resolveClientDateValue(right.createdAt);
        const leftDate = resolveClientDateValue(left.updatedAt) || resolveClientDateValue(left.createdAt);
        return rightDate - leftDate;
    });
}
